/**
 * Текст вложения для просмотра в карточке (журналы записи бага).
 *
 * Ссылка «на просмотр» и сам файл запрашиваются при первом открытии,
 * а не при показе карточки: журналы весят мегабайты, а смотрят их редко.
 * Разобранный файл держится до закрытия карточки.
 */

import { useCallback, useState } from "react";

import { resolveApiUrl } from "@/shared/api/http";

import { useAttachmentOpenUrlMutation } from "@taskflow/client-core/tasks";

export type TextLoad<T> =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; value: T }
  | { kind: "failed" };

export function useAttachmentText<T>(id: string, parse: (text: string) => T | null) {
  const [openUrl] = useAttachmentOpenUrlMutation();
  const [load, setLoad] = useState<TextLoad<T>>({ kind: "idle" });

  const fetchOnce = useCallback(async () => {
    if (load.kind === "ready" || load.kind === "loading") return;
    setLoad({ kind: "loading" });
    try {
      const link = await openUrl({ id }).unwrap();
      const url = resolveApiUrl(link.url);
      if (url === null) throw new Error("нет ссылки");
      const response = await fetch(url);
      if (!response.ok) throw new Error(String(response.status));
      const value = parse(await response.text());
      setLoad(value === null ? { kind: "failed" } : { kind: "ready", value });
    } catch {
      setLoad({ kind: "failed" });
    }
  }, [id, load.kind, openUrl, parse]);

  return { load, fetchOnce };
}
